import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Input, Button } from "reactstrap";
import Select from "react-select";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const EditProduct = () => {

    const { id } = useParams();
    const navigate = useNavigate();
    const user = useSelector(s => s.customer.customer);
    const loading = useSelector(s => s.loading.loading);

    const discountTypes = [
        { value: "flat", label: "Flat" },
        { value: "percentage", label: "Percentage" }
    ];

    const unitOptions = [
        { value: "service", label: "Service" },
        { value: "package", label: "Package" },
        { value: "days", label: "Days" },
        { value: "month", label: "Month" },
        { value: "pcs", label: "Pieces (pcs)" }
    ];

    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);

    const [form, setForm] = useState({
        name: "", sku: "", brand: "", dealer: "", description: "",
        price: "", purchase_price: "", gst_rate: "", stock: "",
        quantity: "", unit: null, low_stock_limit: "",
        discount_type: { value: "percentage", label: "Percentage" },
        discount_value: "0", category: null, sub_category: null
    });

    const [variants, setVariants] = useState([]);

    /* LOAD MASTER DATA */
    useEffect(() => {
        loadCategories();
        loadProduct();
    }, []);

    const loadCategories = async () => {
        const res = await Handler({ method: "get", url: "/categories/list.php?limit=1000" });
        if (res?.success) {
            setCategories(res.data.categories.map(c => ({ value: c.id, label: c.name })));
        }
    };

    const loadSubCategories = async (cid, selectedLabel) => {
        const url = cid ? `/sub_categories/list.php?category_id=${cid}&limit=1000` : `/sub_categories/list.php?limit=2000`;
        const res = await Handler({ method: "get", url });
        if (res?.success) {
            const opts = res.data.sub_categories.map(sc => ({ value: sc.id, label: sc.name }));
            setSubCategories(opts);
            if (selectedLabel) {
                setForm(p => ({ ...p, sub_category: opts.find(o => o.label === selectedLabel) || null }));
            }
        }
    };

    /* LOAD PRODUCT */
    const loadProduct = async () => {
        const res = await Handler({ method: "get", url: `/products/view.php?id=${id}` });
        if (!res?.success) return toast.error("Failed to load service details");

        const d = res.data;
        const sizeStr = d.meta?.size || "";
        const parts = sizeStr.split(" ");
        const qty = parts[0] || "";
        const unitVal = parts[1] || "";

        setForm({
            name: d.name,
            sku: d.meta.sku,
            brand: d.meta.brand || "In-House",
            dealer: d.meta.dealer || "Local",
            description: d.meta.description || "",
            price: d.price,
            purchase_price: d.meta.purchase_price || "0",
            gst_rate: d.gst_rate || "0",
            stock: d.quantity || "0",
            quantity: qty,
            unit: unitOptions.find(u => u.value === unitVal) || null,
            low_stock_limit: d.low_stock_limit || "0",
            discount_type: discountTypes.find(t => t.value === d.meta.discount?.type) || discountTypes[1],
            discount_value: d.meta.discount?.value || "0",
            category: { value: d.category_id, label: d.category_name },
            sub_category: null // loaded via loadSubCategories
        });

        if (d.category_id) loadSubCategories(d.category_id, d.sub_category_name);

        setVariants(
            d.variants?.map(v => ({
                id: v.variant_id,
                name: v.name,
                price: v.price,
                purchase_price: v.meta.purchase_price || "0",
                discount_type: discountTypes.find(t => t.value === v.meta.discount?.type) || discountTypes[1],
                discount_value: v.meta.discount?.value || "0",
                gst_rate: v.gst_rate || "0",
                quantity: v.quantity || "0",
                low_stock_limit: v.low_stock_limit || "0"
            })) || []
        );
    };

    const updateVariant = (i, k, v) => {
        const copy = [...variants];
        copy[i][k] = v;
        setVariants(copy);
    };

    /* SUBMIT */
    const submit = async () => {
        if (!form.name.trim()) return toast.error("Service name required");
        if (!form.category) return toast.error("Category required");
        if (!form.price) return toast.error("Price required");

        const payload = {
            product_id: id,
            name: form.name,
            brand: form.brand,
            dealer: form.dealer,
            price: Number(form.price),
            purchase_price: Number(form.purchase_price || 0),
            discount_type: form.discount_type?.value || "percentage",
            discount_value: Number(form.discount_value || 0),
            gst_rate: Number(form.gst_rate || 0),
            category_id: form.category.value,
            sub_category_id: form.sub_category?.value || "",
            size: form.unit ? `${form.quantity || 1} ${form.unit.value}` : "1 service",
            quantity: Number(form.stock || 0),
            low_stock_limit: Number(form.low_stock_limit || 0),
            description: form.description || "",
            outlet_id: user?.outlet_id,
            variants: variants.map(v => ({
                variant_id: v.id,
                name: v.name,
                price: Number(v.price),
                purchase_price: Number(v.purchase_price || 0),
                discount_type: v.discount_type?.value || "percentage",
                discount_value: Number(v.discount_value || 0),
                gst_rate: Number(v.gst_rate || 0),
                quantity: Number(v.quantity || 0),
                low_stock_limit: Number(v.low_stock_limit || 0)
            }))
        };

        const res = await Handler({ method: "post", url: "/products/update.php", data: payload });
        if (res?.success) {
            toast.success("Service updated successfully");
            navigate("/products");
        } else {
            toast.error(res?.message || "Update failed");
        }
    };

    return (
        <div className="p-3">
            <Row className="mb-4">
                <Col><h4 className="fw-bold">Digital Marketing - Edit Service</h4></Col>
            </Row>

            <Card className="mb-4 border-0 shadow-sm"><CardBody>
                <h6 className="mb-4 text-primary">Core Service Info</h6>
                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <label>Service/Product Name *</label>
                        <Input placeholder="Enter name" value={form.name}
                            onChange={e => setForm({ ...form, name: e.target.value })} />
                    </Col>
                    <Col md={6}>
                        <label>SKU</label>
                        <Input value={form.sku} readOnly className="bg-light" />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <label>Provider/Brand</label>
                        <Input placeholder="Google, Meta, etc." value={form.brand}
                            onChange={e => setForm({ ...form, brand: e.target.value })} />
                    </Col>
                    <Col md={6}>
                        <label>Dealer/Source</label>
                        <Input placeholder="Local/Third party" value={form.dealer}
                            onChange={e => setForm({ ...form, dealer: e.target.value })} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <label>Selling Price *</label>
                        <Input type="number" value={form.price}
                            onChange={e => setForm({ ...form, price: e.target.value })} />
                    </Col>
                    <Col md={4}>
                        <label>Base Cost</label>
                        <Input type="number" value={form.purchase_price}
                            onChange={e => setForm({ ...form, purchase_price: e.target.value })} />
                    </Col>
                    <Col md={4}>
                        <label>GST %</label>
                        <Input type="number" value={form.gst_rate}
                            onChange={e => setForm({ ...form, gst_rate: e.target.value })} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <label>Unit/Duration Type</label>
                        <Select options={unitOptions} value={form.unit}
                            onChange={v => setForm({ ...form, unit: v })} />
                    </Col>
                    <Col md={6}>
                        <label>Duration/Quantity</label>
                        <Input type="number" value={form.quantity}
                            onChange={e => setForm({ ...form, quantity: e.target.value })} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={4}>
                        <label>Category *</label>
                        <Select options={categories} value={form.category}
                            onChange={v => { setForm({ ...form, category: v, sub_category: null }); loadSubCategories(v.value); }} />
                    </Col>
                    <Col md={4}>
                        <label>Sub Category</label>
                        <Select options={subCategories} value={form.sub_category}
                            onChange={v => setForm({ ...form, sub_category: v })} />
                    </Col>
                    <Col md={4}>
                        <label>Discount Type</label>
                        <Select options={discountTypes} value={form.discount_type}
                            onChange={v => setForm({ ...form, discount_type: v })} />
                    </Col>
                </Row>

                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <label>Discount Value</label>
                        <Input type="number" value={form.discount_value}
                            onChange={e => setForm({ ...form, discount_value: e.target.value })} />
                    </Col>
                    <Col md={6}>
                        <label>Status/Stock Qty</label>
                        <Input type="number" value={form.stock}
                            onChange={e => setForm({ ...form, stock: e.target.value })} />
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <label>Service Description</label>
                        <Input type="textarea" rows="3" value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })} />
                    </Col>
                </Row>
            </CardBody></Card>

            {/* ================= PACKAGES (VARIANTS) ================= */}
            {variants.length > 0 && (
                <Card className="mb-4 border-0 shadow-sm"><CardBody>
                    <h6 className="mb-4 text-primary">Service Packages (Variants)</h6>
                    {variants.map((v, i) => (
                        <div key={i} className="border rounded p-3 mb-3 bg-light bg-opacity-50">
                            <Row className="g-3 mb-3">
                                <Col md={4}>
                                    <label className="small fw-bold">Package Name</label>
                                    <Input value={v.name} onChange={e => updateVariant(i, "name", e.target.value)} />
                                </Col>
                                <Col md={4}>
                                    <label className="small fw-bold">Price</label>
                                    <Input type="number" value={v.price} onChange={e => updateVariant(i, "price", e.target.value)} />
                                </Col>
                                <Col md={4}>
                                    <label className="small fw-bold">GST %</label>
                                    <Input type="number" value={v.gst_rate} onChange={e => updateVariant(i, "gst_rate", e.target.value)} />
                                </Col>
                            </Row>
                            <Row className="g-3">
                                <Col md={4}>
                                    <label className="small fw-bold">Discount Type</label>
                                    <Select options={discountTypes} value={v.discount_type} onChange={val => updateVariant(i, "discount_type", val)} />
                                </Col>
                                <Col md={4}>
                                    <label className="small fw-bold">Discount Value</label>
                                    <Input type="number" value={v.discount_value} onChange={e => updateVariant(i, "discount_value", e.target.value)} />
                                </Col>
                                <Col md={4}>
                                    <label className="small fw-bold">Qty/Stock</label>
                                    <Input type="number" value={v.quantity} onChange={e => updateVariant(i, "quantity", e.target.value)} />
                                </Col>
                            </Row>
                        </div>
                    ))}
                </CardBody></Card>
            )}

            <div className="text-end mb-5">
                <Button color="secondary" outline className="me-2" onClick={() => navigate("/products")}>Cancel</Button>
                <Button color="success" size="lg" disabled={loading} onClick={submit}>
                    {loading ? "Updating..." : "Update Service"}
                </Button>
            </div>
        </div>
    );
};

export default EditProduct;